import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { useTableActionContext } from '@aibees/elephant/context/table-action/context';
import { LabelType } from '@aibees/elephant/types/label';
import { DialogActions, DialogTitle } from '@aibees/shared/components';
import { PresetVisibility } from '@aibees/shared/definition';
import { classnames } from '@aibees/shared/utils';
import { DialogContent } from '@mui/material';
import { memo, useMemo } from 'react';
import { useFilterLabelsByOwnershipTab } from '@aibees/elephant/hooks/labels/use-label-ownership-tabs';
import { LabelManagement } from '../search-table-action-dialog-lists/components/label-management';
import { useDisabledClear } from './hooks/use-disabled-clear';
import { useDisabledRemove } from './hooks/use-disabled-remove';
import { useDisabledSelectAll } from './hooks/use-disabled-select-all';
import { useTableSelectionLabels } from './hooks/use-table-selection-labels';
import * as Styled from './styles';
import * as SX from './sx';
import type { SearchTableActionRemoveLabelDialogProps as Props } from './types';

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) =>
  e.stopPropagation();
const DIALOG_PAPER_PROPS = { onMouseDown: stopPropagation };

export const SearchTableActionRemoveLabelDialog = memo(
  ({ className, title, titleHeader, icon, type, state, table }: Props) => {
    const {
      ids,
      handleRemoveLabel,
      handleClose: contextHandleClose,
      removeListDialogState,
      removeLabelDialogState,
    } = useTableActionContext();
    const {
      labels: globalLabels,
      lists: globalLists,
      setLists,
      setLabels,
      tabValue,
      setTabValue,
    } = useTableActionContext();
    const { item } = useActionButtonContext();
    const filterByOwnershipTab = useFilterLabelsByOwnershipTab();
    const isLabel = useMemo(() => type === LabelType.LABEL, [type]);
    const [labels] = useTableSelectionLabels(ids, type, item?.labels);
    const open = useMemo(() => {
      return type === LabelType.LABEL
        ? removeLabelDialogState.value
        : removeListDialogState.value;
    }, [type, removeLabelDialogState.value, removeListDialogState.value]);

    const setItems = useMemo(
      () => (isLabel ? setLabels : setLists),
      [isLabel, setLabels, setLists]
    );

    const disabledSelectAll = useDisabledSelectAll({
      labels,
      isLabel,
    });

    const disabledClear = useDisabledClear({
      labels,
      isLabel,
    });

    const disabledRemoveButton = useDisabledRemove({
      isLabel,
    });

    const handleSubmit = () => {
      const selectedLabels = isLabel ? globalLabels : globalLists;
      handleRemoveLabel(type)(selectedLabels);
    };

    const handleClose = () => {
      contextHandleClose('removeLabel');
      contextHandleClose('removeList');
      setTabValue(PresetVisibility.ALL);
    };

    const handleSelectAll = () => {
      const array = isLabel ? globalLabels : globalLists;
      const setArray = isLabel ? setLabels : setLists;
      const filteredLabels = filterByOwnershipTab(labels, tabValue);

      const filteredLabelsIds = new Set(filteredLabels.map((x) => x.id));
      const remainingArray = array.filter((x) => !filteredLabelsIds.has(x.id));

      setArray([...remainingArray, ...filteredLabels]);
    };

    const handleClear = () => {
      const array = isLabel ? globalLabels : globalLists;
      const setArray = isLabel ? setLabels : setLists;

      const filteredLabels = filterByOwnershipTab(labels, tabValue);

      const filteredArrayIds = new Set(filteredLabels.map((x) => x.id));
      const remainingArray = array.filter((x) => !filteredArrayIds.has(x.id));

      setArray(remainingArray);
    };

    return (
      <Styled.Wrapper
        className={classnames('SearchTableActionRemoveLabelDialog', className)}
        sx={SX.root}
        fullWidth={true}
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        PaperProps={DIALOG_PAPER_PROPS}
      >
        <DialogTitle
          title={titleHeader}
          icon={icon ?? 'list-remove'}
          onClose={handleClose}
        />

        <DialogContent>
          <LabelManagement
            type={type}
            setItems={setItems}
            hasCreateButton={false}
            overrideLabels={labels}
          />
        </DialogContent>

        <DialogActions
          submitTitle="Remove"
          disabled={disabledRemoveButton}
          submitButtonProps={{
            variant: 'contained',
            color: 'error',
          }}
          hasSelectAllButton={true}
          selectAllButtonProps={{
            disabled: disabledSelectAll,
            onClick: handleSelectAll,
          }}
          hasClearButton={true}
          clearButtonProps={{
            children: 'Deselect All',
            disabled: disabledClear,
            onClick: handleClear,
          }}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      </Styled.Wrapper>
    );
  }
);
